import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    FormContentBlockDefaultValuesObject,
    FormValidationSchema,
} from "@/styles";
import { Prompt, Selection, ContentBlock, SelectionShape } from "@/models";

type PromptState = {
    teamId: string;
    simulationId: string;
    prompts: {
        [promptId: string]: Prompt;
    };
    formContentBlocks: { [formContentBlockId: string]: ContentBlock };
    promptsByForm: {
        [formContentBlockId: string]: Prompt[];
    };
    formData: {
        [formContentBlockId: string]: FormContentBlockDefaultValuesObject;
    };
    selectionsByForm: {
        [formContentBlockId: string]: Selection[];
    };
    controlledFormFieldDataMap: {
        [formContentBlockId: string]: {
            [promptId: string]: SelectionShape[];
        };
    };
    formValidationSchemaMap: {
        [formContentBlockId: string]: FormValidationSchema;
    };
    // NOTE: isSubmitting / isInvalid moved to jotai (state/formStatusAtoms.ts).
    // Form validity is now projected from React Hook Form's formState by a
    // single effect, instead of being pushed here via reducers from inside
    // validation passes (which let the red message and the disabled button
    // drift apart).
};

const initialState: PromptState = {
    teamId: "",
    simulationId: "",
    prompts: {},
    formContentBlocks: {},
    promptsByForm: {},
    formData: {},
    selectionsByForm: {},
    controlledFormFieldDataMap: {},
    formValidationSchemaMap: {},
};

const promptsSlice = createSlice({
    name: "SimulationThemeStore",
    initialState,
    reducers: {
        gotIdsForSavingSelections(
            state,
            { payload }: PayloadAction<{ teamId: string; simulationId: string }>
        ) {
            state.teamId = payload.teamId;
            state.simulationId = payload.simulationId;
        },
        gotPrompts(
            state,
            { payload }: PayloadAction<{ [index: string]: Prompt }>
        ) {
            state.prompts = payload;
        },
        gotFormContentBlocks(
            state,
            { payload }: PayloadAction<{ [index: string]: ContentBlock }>
        ) {
            state.formContentBlocks = payload;
        },
        gotPromptsByForm(
            state,
            { payload }: PayloadAction<{ [index: string]: Prompt[] }>
        ) {
            state.promptsByForm = payload;
        },
        gotFormData(
            state,
            {
                payload,
            }: PayloadAction<{
                [index: string]: FormContentBlockDefaultValuesObject;
            }>
        ) {
            state.formData = payload;
        },
        gotSelectionsByForm(
            state,
            { payload }: PayloadAction<{ [index: string]: Selection[] }>
        ) {
            state.selectionsByForm = payload;
        },
        gotControlledFormFieldDataMap(
            state,
            {
                payload,
            }: PayloadAction<{
                [formContentBlockId: string]: {
                    [promptId: string]: SelectionShape[];
                };
            }>
        ) {
            state.controlledFormFieldDataMap = payload;
        },
        gotFormValidationSchemaMap(
            state,
            {
                payload,
            }: PayloadAction<{
                [index: string]: FormValidationSchema;
            }>
        ) {
            state.formValidationSchemaMap = payload;
        },
    },
});

export const promptSliceReducer = promptsSlice.reducer;

// export type RootState = ReturnType<typeof promptSliceReducer>;

const {
    gotIdsForSavingSelections,
    gotPrompts,
    gotFormContentBlocks,
    gotPromptsByForm,
    gotFormData,
    gotSelectionsByForm,
    // gotControlledFormFieldDataMap,
    gotFormValidationSchemaMap,
} = promptsSlice.actions;

export {
    gotIdsForSavingSelections,
    gotPrompts,
    gotFormContentBlocks,
    gotPromptsByForm,
    gotFormData,
    gotSelectionsByForm,
    // gotControlledFormFieldDataMap,
    gotFormValidationSchemaMap,
};
